import { logger } from "@/utils/logger";

const RECOVERY_STORAGE_KEY = "cookpit:chunk-load-recovery";
const RECOVERY_COOLDOWN_MS = 30_000;
const RELOAD_QUERY_PARAM = "__cookpit_reload";

const CHUNK_LOAD_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "failed to load module script",
  "unable to preload css",
  "chunk load failed",
];

type VitePreloadErrorEvent = Event & {
  payload?: unknown;
};

interface RecoveryAttempt {
  href: string;
  message: string;
  timestamp: number;
}

let isPreloadListenerInstalled = false;

export function setupChunkLoadRecovery() {
  if (isPreloadListenerInstalled || typeof window === "undefined") return;

  window.addEventListener("vite:preloadError", (event) => {
    const preloadError = event as VitePreloadErrorEvent;

    if (recoverFromChunkLoadError(preloadError.payload ?? preloadError)) {
      event.preventDefault();
    }
  });

  isPreloadListenerInstalled = true;
}

export function recoverFromChunkLoadError(error: unknown): boolean {
  if (!isChunkLoadError(error) || typeof window === "undefined") {
    return false;
  }

  const now = Date.now();
  const message = stringifyError(error);
  const previousAttempt = readRecoveryAttempt();

  if (previousAttempt && now - previousAttempt.timestamp < RECOVERY_COOLDOWN_MS) {
    logger.warn("Chunk load recovery was already attempted recently:", previousAttempt);
    return false;
  }

  writeRecoveryAttempt({
    href: window.location.href,
    message,
    timestamp: now,
  });

  logger.warn("Detected stale frontend assets. Reloading with a cache buster.", message);
  reloadWithCacheBuster(now);
  return true;
}

export function isChunkLoadError(error: unknown): boolean {
  const message = stringifyError(error).toLowerCase();

  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

function stringifyError(error: unknown): string {
  if (error instanceof Error) {
    return [error.name, error.message, error.stack].filter(Boolean).join(" ");
  }

  if (typeof error === "string") {
    return error;
  }

  try {
    return JSON.stringify(error) || "";
  } catch {
    return String(error);
  }
}

function readRecoveryAttempt(): RecoveryAttempt | null {
  try {
    const rawValue = window.sessionStorage.getItem(RECOVERY_STORAGE_KEY);
    if (!rawValue) return null;

    const attempt = JSON.parse(rawValue) as Partial<RecoveryAttempt>;
    if (!attempt.timestamp || typeof attempt.timestamp !== "number") return null;

    return {
      href: String(attempt.href || ""),
      message: String(attempt.message || ""),
      timestamp: attempt.timestamp,
    };
  } catch {
    return null;
  }
}

function writeRecoveryAttempt(attempt: RecoveryAttempt) {
  try {
    window.sessionStorage.setItem(RECOVERY_STORAGE_KEY, JSON.stringify(attempt));
  } catch {
    // Storage can be unavailable in restricted browser modes; the reload still works.
  }
}

function reloadWithCacheBuster(timestamp: number) {
  try {
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set(RELOAD_QUERY_PARAM, String(timestamp));
    window.location.replace(nextUrl.toString());
  } catch (error) {
    logger.warn("Cache-busted reload failed, falling back to a normal reload:", error);
    window.location.reload();
  }
}
